// lib/widgets/registry.js
// ═══════════════════════════════════════════════════════════
// WIDGET REGISTRY — single source of truth for all widgets
// ═══════════════════════════════════════════════════════════
// Every widget in Shift registers here with its metadata:
//   key                — stable id used in workstation config
//   name               — user-facing title
//   description        — what the widget does (1 line)
//   tier               — A | B | C | D (per UNIVERSAL_WIDGETS.md)
//   category           — grouping for the Library UI
//   suitable_for       — which workstation types show it
//   built              — true if shipping; false = placeholder/coming-soon
//   size_classes       — ['S','M','L'] variants (future)
//   default_on         — per-workstation-type auto-include
//
// When a new widget is built, ADD an entry here with built: true.
// When a new widget is planned, ADD an entry with built: false so
// it shows up in the Library's "Coming Soon" section.
// ═══════════════════════════════════════════════════════════

export const TIER = {
  A: 'Jarvis Surface',
  B: 'Operational Universal',
  C: 'Pattern-Universal',
  D: 'Domain-Specific',
};

export const WIDGETS = [
  // ───────────────────────── TIER A (Jarvis Surface) ─────────────────────────
  {
    key: 'jarvis_briefing',
    name: 'Today\u2019s Briefing',
    description: 'The 5-point morning briefing — what\u2019s happening, options, misdirections, possibilities. Jarvis\u2019s primary view.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching', 'real_estate', 'law'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 12, h: 14, minW: 6, minH: 8 },
    body: { minHeight: 300, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'daily_read',
    name: 'Today\u2019s Read',
    description: 'Sonnet-generated domain brief — market direction, your direction, the gap, watch list, what to ignore, and the move drivers behind QQQ/SPY today.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['L'],
    // Standard 560 cap — body scrolls inside the card, expandable
    // modal for the full read. Giant cards break the dashboard
    // rhythm; scroll is the right affordance for rich long-form.
    default_layout: { w: 12, h: 10, minW: 6, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'theses',
    name: 'Theses',
    description: 'State long-term strategic premises. Engines 3/4/6 link to these — tensions flag when evidence drifts, options cite them, possibilities use them as anchors.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching', 'real_estate', 'law'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 12, h: 10, minW: 6, minH: 6 },
    body: { minHeight: 240, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },

  // ───────────────────────── TIER B (Operational Universal) ─────────────────────────
  {
    key: 'process_checklist',
    name: 'Process Checklist',
    description: 'Your hard rules, surfaced before action. Enforces what you said you\u2019d do.',
    tier: 'B', category: 'rules',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['S', 'M'],
    default_layout: { w: 6, h: 6, minW: 4, minH: 4 },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'weekly_review',
    name: 'Weekly Review',
    description: 'Sunday sync. Pattern activity, behaviors, outcomes, one question for next week.',
    tier: 'B', category: 'rituals',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 12, h: 8, minW: 6, minH: 5 },
    body: { minHeight: 240, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },

  // ───────────────────────── TIER C (Pattern-Universal) ─────────────────────────
  {
    key: 'force_tracker',
    name: 'Force Tracker',
    description: 'The contextual forces moving your domain today. Trading uses a 7-force classifier (VIX, rates, breadth, sentiment, dollar, earnings, premarket).',
    tier: 'C', category: 'context',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'pattern_library_status',
    name: 'Pattern Library Status',
    description: 'How many sessions have been logged, how many patterns are firing, where validation stands.',
    tier: 'C', category: 'learning',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['S', 'M'],
    default_layout: { w: 4, h: 4, minW: 3, minH: 3 },
    default_on: { day_trading: true, long_term: false },
  },

  // ───────────────────────── TIER D — Trading (Day) ─────────────────────────
  {
    key: 'thesis',
    name: 'Today\u2019s Thesis',
    description: 'Your single thesis for the trading day. Written pre-market, reviewed at close.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['S', 'M'],
    default_layout: { w: 6, h: 5, minW: 4, minH: 3 },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'studies',
    name: 'Studies Confluence',
    description: 'Which of your 8 anchor studies are confirming today. Confluence threshold gates entry.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['S', 'M'],
    default_layout: { w: 6, h: 5, minW: 4, minH: 3 },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'window_risk_map',
    name: 'Window Risk Map',
    description: 'Events inside your trading window — FOMC, earnings, macro catalysts.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['S', 'M'],
    default_layout: { w: 4, h: 5, minW: 3, minH: 3 },
    default_on: { day_trading: true, long_term: false },
  },

  // ───────────────────────── TIER D — Trading (Long-Term) ─────────────────────────
  {
    key: 'macro_gate',
    name: 'Macro Gate',
    description: 'EHIQ Structural Risk Index. Deploy / Hold / Caution / Protect mode based on macro stress.',
    tier: 'D', category: 'long-term',
    suitable_for: ['long_term'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 8, minW: 4, minH: 6 },
    default_on: { day_trading: false, long_term: true },
  },
  {
    key: 'pipeline_intelligence',
    name: 'Pipeline Intelligence',
    description: 'Your 5-stage company pipeline — Scout / Monitor / Pilot / Conviction / Max.',
    tier: 'D', category: 'long-term',
    suitable_for: ['long_term'],
    built: true,
    size_classes: ['L'],
    default_layout: { w: 12, h: 10, minW: 8, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: false, long_term: true },
  },
  {
    key: 'pre_explosion_matrix',
    name: 'Pre-Explosion Matrix',
    description: 'The 6-signal matrix — which pre-explosion signals are firing across your pipeline.',
    tier: 'D', category: 'long-term',
    suitable_for: ['long_term'],
    built: true,
    size_classes: ['L'],
    default_layout: { w: 12, h: 12, minW: 8, minH: 8 },
    body: { minHeight: 280, expandable: true },
    default_on: { day_trading: false, long_term: true },
  },

  // ───────────────────────── COMING SOON (placeholders — built: false) ─────────────────────────
  {
    key: 'strategy_health',
    name: 'Strategy Health Scorecard',
    description: 'Five edge-health dimensions (win rate, expectancy, rule compliance, drawdown, consec losses) graded green / yellow / red with deltas vs the prior window. Overall grade is the worst of the five.',
    tier: 'B', category: 'strategy-health',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'kill_switch',
    name: 'Kill Switch',
    description: 'Locks new entries after N consecutive losses or a daily loss limit (USD or R). Override requires a typed reason \u2014 logged for the Weekly Review to surface override frequency.',
    tier: 'B', category: 'safety',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['M'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 280, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
    {
      key: 'risk_budget',
      name: 'Risk Budget',
      description: 'Daily / weekly / monthly loss budgets in USD and R with live progress bars. Soft sibling of the Kill Switch \u2014 shows the slope toward the hard lock before you hit it.',
      tier: 'B', category: 'safety',
      suitable_for: ['trading', 'long_term'],
      built: true,
      size_classes: ['M', 'L'],
      default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
      body: { minHeight: 320, expandable: true },
      default_on: { day_trading: true, long_term: true },
    },
  {
    key: 'morning_routine',
    name: 'Morning Routine',
    description: 'Pre-market checklist: mental-state rung (1\u20135), news scan, levels, setups, thesis, risk budget. Ready tag gates the session. 10-day history strip shows streak + skipped days.',
    tier: 'B', category: 'rituals',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'mistake_log',
    name: 'Mistake Log',
    description: 'Reflective log of rule violations, bad entries, chased trades, size + exit mistakes, emotional overrides. Each entry: what, why, cost, and the lesson \u2014 the line that matters. Grouped by kind with window tally.',
    tier: 'B', category: 'learning',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'decision_journal',
    name: 'Decision Journal',
    description: 'Every consequential decision with reasoning + expected outcome + confidence. Passes and skips count. Resolve later with the actual outcome \u2014 builds the evidence log for your edge.',
    tier: 'B', category: 'learning',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'personal_playbook',
    name: 'Personal Playbook',
    description: 'Validated patterns as readable one-pagers (when it fires / mechanics / entry / exit / sizing / what works / what breaks). Re-read before the bell. 60-day stale flag surfaces drift.',
    tier: 'B', category: 'learning',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 12, h: 14, minW: 6, minH: 8 },
    body: { minHeight: 340, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'thesis_stress_test',
    name: 'Thesis Stress Test',
    description: 'Live health of every active thesis \u2014 pressure gauge, drift drivers, open tensions, and a single score per thesis. Read-only monitoring view; manage in the Theses widget.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching', 'real_estate', 'law'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'strategic_dialogue',
    name: 'Strategic Dialogue',
    description: 'Persistent chat thread \u2014 full 5-layer workstation context injected on every turn (criteria, theses, tensions, patterns, positions, force snapshot). Reason WITH your AI, not AT it.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching', 'real_estate', 'law'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 12, h: 14, minW: 6, minH: 10 },
    body: { minHeight: 380, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'position_sizing',
    name: 'Position Sizing',
    description: 'Position size given entry, stop, VIX, account. Auto-applies Deploy/Hold + correlation penalties.',
    tier: 'C', category: 'sizing',
    suitable_for: ['trading', 'long_term'],
    built: false, coming_in: 'Batch 7',
  },
  {
    key: 'outcome_calendar_heatmap',
    name: 'Outcome Calendar Heatmap',
    description: 'Month grid of closed-trade P&L, color-coded sage (green) / terra (red) with intensity by magnitude. Click a day to see the individual trades. Scan for clusters, streaks, and day-of-week edge.',
    tier: 'C', category: 'analytics',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 280, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'entity_deep_dive_workspace',
    name: 'Entity Deep Dive',
    description: 'Full per-entity research workspace. Thesis / data / documents / timeline / Jarvis chat.',
    tier: 'C', category: 'research',
    suitable_for: ['long_term', 'medicine', 'real_estate', 'law'],
    built: false, coming_in: 'Batch 8',
  },
  {
    key: 'news_correlation',
    name: 'News Correlation',
    description: 'Recent feed items that mention YOUR tickers (config.criteria.instruments \u222A watchlist). Word-boundary scan, grouped by ticker, click to filter. V2 layers Sonnet bullish/bearish + impact classification.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'dte_conditions',
    name: '0DTE Conditions',
    description: 'Day-of context for 0DTE: session phase clock, VIX term structure, CBOE PCR, spot prints, high-impact macro events. Pin levels + gamma walls + dealer GEX render as labeled placeholders pending V2 paid-data wiring (SpotGamma / Unusual Whales / Tier1Alpha).',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'gex_levels',
    name: 'GEX + Dealer Levels',
    description: 'Options exposure analytics from FlashAlpha Lab API. Underlying price, net gamma exposure, gamma flip level, strike-by-strike GEX bars, key dealer levels (call wall / put wall / zero gamma), max pain, narrative interpretation. Symbol tabs — TSLA validates Free tier; SPY/QQQ once paid tier is active.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 12, minW: 4, minH: 8 },
    body: { minHeight: 320, expandable: true },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'pre_market',
    name: 'Pre-Market Read',
    description: 'Overnight + pre-open scan: US futures, international markets, VIX term structure, today\u2019s high-impact events, net bias.',
    tier: 'D', category: 'day-trading',
    suitable_for: ['trading'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: true, long_term: false },
  },
  {
    key: 'sentiment',
    name: 'Sentiment',
    description: 'Fear & greed gauge + AAII bulls/bears + COT net positioning + short interest. Where consensus sits right now and which edge of the crowd is fragile.',
    tier: 'C', category: 'context',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'macro_conditions',
    name: 'Macro Conditions',
    description: 'Yield curve + rates & credit spreads + dollar + commodities + the economic scoreboard picks. The backdrop your strategy lives inside.',
    tier: 'C', category: 'context',
    suitable_for: ['trading', 'long_term'],
    built: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: true, long_term: true },
  },
  {
    key: 'new_companies_flagged',
    name: 'New Companies Flagged',
    description: 'Companies newly crossing into your pipeline (Scout stage) \u2014 classifier-flagged, awaiting your read.',
    tier: 'D', category: 'long-term',
    suitable_for: ['long_term'],
    built: false, coming_in: 'Phase 3b',
    default_on: { day_trading: false, long_term: false },
  },
  {
    // Alias for thesis_stress_test \u2014 kept so earlier dashboard
    // configs that referenced thesis_health keep working. Renders
    // the same component. Don't add this to the Library; users
    // should pick thesis_stress_test going forward.
    key: 'thesis_health',
    name: 'Thesis Health',
    description: 'Alias for Thesis Stress Test. Prefer that widget \u2014 this entry is retained for backward compatibility.',
    tier: 'A', category: 'jarvis-surface',
    suitable_for: ['trading', 'long_term', 'medicine', 'teaching', 'real_estate', 'law'],
    built: true,
    hidden_in_library: true,
    size_classes: ['M', 'L'],
    default_layout: { w: 6, h: 10, minW: 4, minH: 6 },
    body: { minHeight: 260, expandable: true },
    default_on: { day_trading: false, long_term: false },
  },
];

// ═══════════════════════════════════════════════════════════
// LOOKUPS
// ═══════════════════════════════════════════════════════════

const BY_KEY = Object.fromEntries(WIDGETS.map((w) => [w.key, w]));

/** Get widget metadata by key. Returns null if unknown. */
export function getWidget(key) {
  return BY_KEY[key] || null;
}

/** List all widgets (optionally filtered). */
export function listWidgets({ tier, built, suitable_for, category } = {}) {
  return WIDGETS.filter((w) => {
    if (tier && w.tier !== tier) return false;
    if (built !== undefined && w.built !== built) return false;
    if (suitable_for && !(w.suitable_for || []).includes(suitable_for)) return false;
    if (category && w.category !== category) return false;
    return true;
  });
}

/**
 * Resolve the LIBRARY VIEW for a given workstation.
 * Returns: { active, available, coming_soon, other }
 *   active      — widgets currently on this workstation's dashboard (order preserved)
 *   available   — built, suitable, not currently active
 *   coming_soon — suitable but not built yet
 *   other       — built but not suitable for this workstation (collapsed by default in UI)
 */
export function resolveLibrary(workstation) {
  const slug = workstation?.slug || '';
  const domainKey = workstation?.domain_key || 'trading';
  const activeKeys = workstation?.config?.tab_config?.dashboard?.sections || [];
  const active = [];
  const available = [];
  const coming_soon = [];
  const other = [];

  // workstation-type inference for suitability filtering
  const wsType = inferWsType(slug, domainKey);

  // Active list — preserve order, only include known keys
  for (const key of activeKeys) {
    const w = BY_KEY[key];
    if (w) active.push({ ...w, status: 'active' });
  }

  // Scan the registry for available / coming_soon / other
  for (const w of WIDGETS) {
    if (activeKeys.includes(w.key)) continue; // already in active
    const suitable = isSuitable(w, wsType);
    if (!w.built) {
      if (suitable) coming_soon.push({ ...w, status: 'coming_soon' });
      // Unsuitable + not built → drop
      continue;
    }
    if (suitable) available.push({ ...w, status: 'available' });
    else other.push({ ...w, status: 'other' });
  }

  return { active, available, coming_soon, other, ws_type: wsType };
}

function inferWsType(slug, domainKey) {
  if (slug.includes('long-term') || slug.includes('long_term')) return 'long_term';
  if (slug.includes('day') || slug.includes('intraday')) return 'day_trading';
  if (domainKey === 'trading') return 'day_trading'; // default trading workstation
  return domainKey || 'other';
}

function isSuitable(w, wsType) {
  if (!Array.isArray(w.suitable_for)) return false;
  // Long-Term maps to 'long_term', Day Trading to 'trading' (legacy) or 'day_trading'
  if (w.suitable_for.includes(wsType)) return true;
  if (wsType === 'day_trading' && w.suitable_for.includes('trading')) return true;
  if (wsType === 'long_term' && w.suitable_for.includes('trading') && (w.suitable_for.includes('long_term') || w.tier === 'A' || w.tier === 'B')) return true;
  return false;
}

export const CATEGORY_LABELS = {
  'jarvis-surface': 'Jarvis Surface',
  'rules': 'Rules & Discipline',
  'rituals': 'Rituals',
  'context': 'Market Context',
  'learning': 'Learning Loop',
  'safety': 'Safety',
  'sizing': 'Sizing & Execution',
  'analytics': 'Analytics',
  'research': 'Research',
  'day-trading': 'Day Trading',
  'long-term': 'Long-Term',
  'strategy-health': 'Strategy Health',
};

// Unicode glyphs per category — used by widget-card header.
// A widget can override with an explicit `icon` field in its
// registry entry; otherwise the glyph falls back to this map.
export const CATEGORY_GLYPHS = {
  'jarvis-surface': '\u25C6',     // ◆  — primary intelligence
  'rules':          '\u2318',     // ⌘  — rules / enforcement
  'rituals':        '\u2295',     // ⊕  — review / reflection / closing loop
  'context':        '\u25C8',     // ◈  — forces / macro context
  'learning':       '\u26A1',     // ⚡ — signals / patterns
  'safety':         '\u25CE',     // ◎  — gates / guards
  'sizing':         '\u26A1',     // ⚡ — execution action
  'analytics':      '\u25F1',     // ◱  — data grids
  'research':       '\u25B8',     // ▸  — progression / deep dive
  'day-trading':    '\u25C9',     // ◉  — focus / today
  'long-term':      '\u25B8',     // ▸  — progression / pipeline
  'strategy-health': '\u25CE',    // ◎  — green/yellow/red gate
};

/** Resolve the header glyph for a widget. */
export function widgetGlyph(widget) {
  if (!widget) return '\u25E6'; // ◦
  if (widget.icon) return widget.icon;
  return CATEGORY_GLYPHS[widget.category] || '\u25E6';
}
